import React from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Text,
  Input,
  Field,
  tokens,
} from '@fluentui/react-components';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  isDangerous?: boolean;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface>
        <DialogTitle>{title}</DialogTitle>
        <DialogBody>
          <DialogContent>
            {typeof message === 'string' ? <Text>{message}</Text> : message}
          </DialogContent>
        </DialogBody>
        <DialogActions>
          <Button appearance="secondary" onClick={onClose}>
            {cancelText}
          </Button>
          <Button appearance="primary" onClick={onConfirm}>
            {confirmText}
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};

interface DeleteColumnDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  columnTitle: string;
  activeTaskCount: number;
}

export const DeleteColumnDialog: React.FC<DeleteColumnDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  columnTitle,
  activeTaskCount,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface>
        <DialogTitle>Delete Column</DialogTitle>
        <DialogBody>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Text>
                Are you sure you want to delete the "{columnTitle}" column?
              </Text>
              {activeTaskCount > 0 && (
                <Text style={{ color: tokens.colorPaletteRedForeground1, fontWeight: 'semibold' }}>
                  This will permanently delete {activeTaskCount} task{activeTaskCount > 1 ? 's' : ''} in this column.
                </Text>
              )}
              <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
                This action cannot be undone.
              </Text>
            </div>
          </DialogContent>
        </DialogBody>
        <DialogActions>
          <Button appearance="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button appearance="primary" onClick={onConfirm}>
            {activeTaskCount > 0
              ? `Delete Column & ${activeTaskCount} Task${activeTaskCount > 1 ? 's' : ''}`
              : 'Delete Column'}
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};

interface DeleteRowDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  rowName: string;
  activeTaskCount: number;
}

export const DeleteRowDialog: React.FC<DeleteRowDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  rowName,
  activeTaskCount,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface>
        <DialogTitle>Delete Row</DialogTitle>
        <DialogBody>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Text>
                Are you sure you want to delete the "{rowName}" row?
              </Text>
              {activeTaskCount > 0 && (
                <Text style={{ color: tokens.colorPaletteRedForeground1, fontWeight: 'semibold' }}>
                  This will permanently delete {activeTaskCount} task{activeTaskCount > 1 ? 's' : ''} in this row.
                </Text>
              )}
              <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
                This action cannot be undone.
              </Text>
            </div>
          </DialogContent>
        </DialogBody>
        <DialogActions>
          <Button appearance="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button appearance="primary" onClick={onConfirm}>
            {activeTaskCount > 0
              ? `Delete Row & ${activeTaskCount} Task${activeTaskCount > 1 ? 's' : ''}`
              : 'Delete Row'}
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};

interface DeleteTaskDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  taskTitle: string;
}

export const DeleteTaskDialog: React.FC<DeleteTaskDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  taskTitle,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface>
        <DialogTitle>Delete Task</DialogTitle>
        <DialogBody>
          <DialogContent>
            <Text>
              Are you sure you want to delete the "{taskTitle}" task?
              This action cannot be undone.
            </Text>
          </DialogContent>
        </DialogBody>
        <DialogActions>
          <Button appearance="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button appearance="primary" onClick={onConfirm}>
            Delete Task
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};

interface AddColumnDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  columnTitle: string;
  setColumnTitle: (title: string) => void;
}

export const AddColumnDialog: React.FC<AddColumnDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  columnTitle,
  setColumnTitle,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface>
        <DialogTitle>Add New Column</DialogTitle>
        <DialogBody>
          <DialogContent style={{ marginBottom: '1rem' }}>
            <Field label="Column Title" required>
              <Input
                value={columnTitle}
                onChange={(_, data) => setColumnTitle(data.value)}
                placeholder="Enter column title"
                autoFocus
              />
            </Field>
          </DialogContent>
        </DialogBody>
        <DialogActions>
          <Button appearance="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button appearance="primary" onClick={onConfirm} disabled={!columnTitle.trim()}>
            Add Column
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};

interface AddRowDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  rowName: string;
  setRowName: (name: string) => void;
}

export const AddRowDialog: React.FC<AddRowDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  rowName,
  setRowName,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface>
        <DialogTitle>Add New Row</DialogTitle>
        <DialogBody>
          <DialogContent style={{ marginBottom: '1rem' }}>
            <Field label="Row Name" required>
              <Input
                value={rowName}
                onChange={(_, data) => setRowName(data.value)}
                placeholder="Enter row name"
                autoFocus
              />
            </Field>
          </DialogContent>
        </DialogBody>
        <DialogActions>
          <Button appearance="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button appearance="primary" onClick={onConfirm} disabled={!rowName.trim()}>
            Add Row
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};

interface ArchiveConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  itemType: 'column' | 'row';
  itemName: string;
  taskCount: number;
}

export const ArchiveConfirmDialog: React.FC<ArchiveConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  itemType,
  itemName,
  taskCount,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface>
        <DialogTitle>Archive Tasks</DialogTitle>
        <DialogBody>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Text>
                Are you sure you want to archive {taskCount} task{taskCount !== 1 ? 's' : ''} from the "{itemName}" {itemType}?
              </Text>
              <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
                Archived tasks can be restored from the Archive view.
              </Text>
            </div>
          </DialogContent>
        </DialogBody>
        <DialogActions>
          <Button appearance="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button appearance="primary" onClick={onConfirm}>
            Archive {taskCount} Task{taskCount !== 1 ? 's' : ''}
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};

interface ErrorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
}

export const ErrorDialog: React.FC<ErrorDialogProps> = ({
  isOpen,
  onClose,
  title,
  message,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface>
        <DialogTitle>{title}</DialogTitle>
        <DialogBody>
          <DialogContent>
            <Text>{message}</Text>
          </DialogContent>
        </DialogBody>
        <DialogActions>
          <Button appearance="primary" onClick={onClose}>
            OK
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};
